import { createSelector } from 'reselect';

import { getCurrentUser, getCurrentUserRootBoardId } from '../../../../../user/currentUserSelector';
import { getElements } from '../../../../../element/selectors/elementsSelector';
import { SearchResult } from '../boardSearchPopupTypes';
import { getElementId, getElementType, getTitle } from '../../../../../../common/elements/utils/elementPropertyUtils';
import {
    boardIdToAliasIdsMapSelector,
    boardVisibleElementGraphSelector,
} from '../../../../../element/selectors/elementGraphSelector';
import { getSortedViewableViewedBoardIds } from '../../../../../user/navigationHistory/navigationHistorySelector';

import { BreadcrumbResult, getBreadcrumb } from '../../../../breadcrumbs/breadcrumbUtil';
import { getAclIdsSelector } from '../../../../../utils/permissions/permissionsSelector';
import { createDeepSelector, createShallowSelector } from '../../../../../utils/milanoteReselect/milanoteReselect';
import { getFeatureFlag } from '../../../../../../common/users/utils/userPropertyUtils';
import { getIsAsyncResourceEntityFetched } from '../../../../../utils/services/http/asyncResource/asyncResourceSelector';
import { breadcrumbSelector } from '../../../../breadcrumbs/breadcrumbSelector';

// Types
import { ElementCountMap } from '../../../../../../common/elements/elementCountsUtils';
import { ImMNElementMap } from '../../../../../../common/elements/elementModelTypes';
import { ResourceTypes } from '../../../../../utils/services/http/asyncResource/asyncResourceConstants';
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';
import { BoardSearchResultAncestor } from '../../../../../../common/boards/search/boardSearchTypes';
import { prop } from '../../../../../../common/utils/immutableHelper';
import { IdGraph } from '../../../../../../common/dataStructures/graphTypes';
import { ElementType } from '../../../../../../common/elements/elementTypes';

export const boardSearchBreadcrumbAncestorsSelector = createDeepSelector(
    breadcrumbSelector,
    getElements,
    (breadcrumb: BreadcrumbResult, elements): BoardSearchResultAncestor[] => {
        if (!breadcrumb?.path) return [];

        return breadcrumb.path.map((elementId) => ({
            _id: elementId,
            title: getTitle(prop(elementId, elements)) || '',
        }));
    },
);

export const selectBoardSearchMigrationComplete = (state: unknown): boolean => {
    const currentUser = getCurrentUser(state);
    const hasMigrated = getFeatureFlag(ExperimentId.boardSearchIndexComplete)(currentUser);
    return hasMigrated;
};

const getChildrenCounts = (
    boardId: string,
    boardVisibleGraph: { [key: string]: string[] },
    elements: ImMNElementMap,
): ElementCountMap | undefined => {
    const childrenIds = boardVisibleGraph[boardId];
    if (!childrenIds) return undefined;

    return childrenIds.reduce((counts: ElementCountMap, childId: string) => {
        const element = elements.get(childId);
        if (!element) return counts;

        const elementType = getElementType(element) as ElementType;
        counts[elementType] = (counts[elementType] || 0) + 1;

        return counts;
    }, {});
};

/**
 * Enriches the list of recent boards with the ancestors and element counts
 */
const enrichedRecentBoards = createShallowSelector(
    getSortedViewableViewedBoardIds,
    getElements,
    boardVisibleElementGraphSelector,
    getCurrentUserRootBoardId,
    getAclIdsSelector,
    boardIdToAliasIdsMapSelector,
    (
        recentBoardIds,
        elements,
        boardVisibleGraph: { [key: string]: any },
        rootBoardId,
        aclIds,
        boardIdToAliasIdsMap,
    ) => {
        return recentBoardIds
            .map((boardId) => elements.get(boardId))
            .filter((board) => !!board)
            .map((board) => {
                const breadcrumb = getBreadcrumb(
                    getElementId(board),
                    rootBoardId,
                    elements,
                    aclIds,
                    boardIdToAliasIdsMap as IdGraph,
                );

                const ancestors = breadcrumb.path
                    ?.reverse()
                    .slice(1, 4)
                    .map((ancestorId) => ({
                        _id: ancestorId,
                        title: getTitle(elements.get(ancestorId)),
                    }));

                return {
                    board: board,
                    elementCounts: getChildrenCounts(getElementId(board), boardVisibleGraph, elements),
                    ancestors,
                };
            });
    },
);

/**
 * Format the recent boards into an array of SearchResult[], with a header
 */
export const recentBoardResultsSelector = createSelector(enrichedRecentBoards, (boards): SearchResult[] => {
    const boardResults = boards.map(({ board, elementCounts, ancestors }): SearchResult => {
        return {
            _id: getElementId(board),
            type: 'entry',
            result: {
                _id: getElementId(board),
                board: board.toJS(),
                ancestors: ancestors || [],
                innerHits: [],
                userIds: [],
                elementCounts: elementCounts || {},
                score: 0,
                viewedTime: 0,
                modifiedTime: 0,
            },
        };
    });

    return [
        {
            _id: 'recent',
            type: 'date',
            date: new Date(),
            description: 'Recently viewed',
        },
        ...boardResults,
    ];
});

export const recentBoardsFetchedSelector = (state: any): boolean =>
    getIsAsyncResourceEntityFetched(state, ResourceTypes.history);
